import { Controller } from '@hotwired/stimulus';

// Hides the sticky footer total when the sidebar total is on screen (scrolled to the bottom on
// mobile), so the amount is not shown twice.
export default class extends Controller {
  static targets = ['navTotal', 'scrollRoot'];

  connect() {
    this.sidebarTotalVisible = false;
    this.sync = this.sync.bind(this);
    this.reobserve = this.reobserve.bind(this);
    this.onScroll = this.sync;

    this.observer = new IntersectionObserver(
      ([entry]) => {
        this.sidebarTotalVisible = entry?.isIntersecting ?? false;
        this.sync();
      },
      {
        // Observe against the checkout scrollport (not the viewport) — that is what moves on mobile.
        root: this.hasScrollRootTarget ? this.scrollRootTarget : null,
        threshold: 0,
      }
    );

    if (this.hasScrollRootTarget) {
      this.scrollRootTarget.addEventListener('scroll', this.onScroll, { passive: true });
    }

    this.reobserve();
    document.addEventListener('turbo:after-stream-render', this.reobserve);
  }

  disconnect() {
    document.removeEventListener('turbo:after-stream-render', this.reobserve);
    this.scrollRootTarget?.removeEventListener('scroll', this.onScroll);
    this.observer?.disconnect();
  }

  reobserve() {
    this.observer.disconnect();
    const sidebarTotal = this.element.querySelector('#order-sidebar-total');
    if (sidebarTotal) this.observer.observe(sidebarTotal);
    this.sync();
  }

  sync() {
    if (!this.hasNavTotalTarget) return;
    this.navTotalTarget.hidden = this.sidebarTotalVisible || this.isAtBottom();
  }

  isAtBottom() {
    if (!this.hasScrollRootTarget) return false;

    const { scrollTop, scrollHeight, clientHeight } = this.scrollRootTarget;
    const maxScroll = scrollHeight - clientHeight;
    // No overflow (desktop grid): don't treat as "at bottom" — IO handles the sidebar column.
    if (maxScroll <= 1) return false;

    return scrollTop >= maxScroll - 80;
  }
}
